import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  People as PeopleIcon,
  ShoppingCartCheckout as CartIcon,
  Category as SegmentIcon,
  Star as VipIcon,
  AttachMoney as MoneyIcon,
  Chat as LineIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import RetentionSection from '../components/retention/RetentionSection';
import AbandonedCartSection from '../components/retention/AbandonedCartSection';
import SegmentsSection from '../components/retention/SegmentsSection';
import VipSection from '../components/retention/VipSection';
import ChannelMarginSection from '../components/retention/ChannelMarginSection';
import LineLinkSection from '../components/retention/LineLinkSection';

const OWNER_USER_ID = 1;

const TABS = [
  { key: 'retention', label: '留存', icon: <PeopleIcon fontSize="small" /> },
  { key: 'abandoned', label: '棄單', icon: <CartIcon fontSize="small" /> },
  { key: 'segments', label: '客群分析', icon: <SegmentIcon fontSize="small" /> },
  { key: 'vip', label: 'VIP 會員', icon: <VipIcon fontSize="small" /> },
  { key: 'channel', label: '通路與利潤', icon: <MoneyIcon fontSize="small" /> },
  { key: 'line', label: 'LINE 綁定', icon: <LineIcon fontSize="small" /> },
];

const CustomerRetentionPage: React.FC = () => {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const [tab, setTab] = useState<string>('retention');
  const [refreshKey, setRefreshKey] = useState(0);

  // Owner-only page: only user id 1 (Magnus) can access, same pattern as Shopify Invoice
  if (currentUser && currentUser.id !== OWNER_USER_ID) {
    return <Navigate to="/" replace />;
  }

  const handleRefresh = () => setRefreshKey((k) => k + 1);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            顧客與留存
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            官網回頭客、棄單、VIP、通路利潤一站追蹤。Q2 目標：留存率 18% → 22%。
          </Typography>
        </Box>
        <IconButton
          onClick={handleRefresh}
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15) },
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 3,
          '& .MuiTab-root': { minHeight: 48, textTransform: 'none', fontWeight: 500 },
        }}
      >
        {TABS.map((t) => (
          <Tab
            key={t.key}
            value={t.key}
            label={t.label}
            icon={t.icon}
            iconPosition="start"
          />
        ))}
      </Tabs>

      <Box>
        {tab === 'retention' && <RetentionSection refreshKey={refreshKey} />}
        {tab === 'abandoned' && <AbandonedCartSection refreshKey={refreshKey} />}
        {tab === 'segments' && <SegmentsSection refreshKey={refreshKey} />}
        {tab === 'vip' && <VipSection refreshKey={refreshKey} />}
        {tab === 'channel' && <ChannelMarginSection refreshKey={refreshKey} />}
        {tab === 'line' && <LineLinkSection refreshKey={refreshKey} />}
      </Box>
    </Box>
  );
};

export default CustomerRetentionPage;
